import { useCallback, useEffect, useMemo, useState, type MouseEventHandler } from 'react';

import { useGetSensorsDataByEventAndIds } from '@realTimeDataAccess';
import { FlexAligner, IconItem, Typography } from '@scaleaq/scaleui';
import {
  AspectRatio,
  CameraInfo,
  FeedingDeviceChanged,
  FeedingDeviceInfo,
  ISiteControlDeviceChanged,
} from '@scaleaq/sitecontrol-lib';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { CameraSensorReading } from '@/components/cameraStreamWidget/CameraSensorReading.tsx';
import { CameraSnapshotsOverlay } from '@/components/cameraStreamWidget/CameraSnapshotsOverlay.tsx';
import { CameraStreamDvrControls } from '@/components/cameraStreamWidget/CameraStreamDvrControls.tsx';
import { CameraStreamPauseToggleButton } from '@/components/cameraStreamWidget/CameraStreamPauseToggleButton.tsx';
import { CameraStreamSideControls } from '@/components/cameraStreamWidget/CameraStreamSideControls.tsx';
import { getVideoPauseToggle } from '@/components/cameraStreamWidget/cameraStreamVideoPause.helpers.ts';
import { CameraStreamWebRtcStatus } from '@/components/cameraStreamWidget/CameraStreamWebRtcStatus.tsx';
import { CameraStreamWinchStatus } from '@/components/cameraStreamWidget/CameraStreamWinchStatus.tsx';
import type { CameraSwitching } from '@/components/cameraStreamWidget/cameraSwitchPanel/cameraSwitchPanel.types.ts';
import { canPlayHlsRecall } from '@/components/cameraStreamWidget/canPlayHevcMse.ts';
import { FeedStatusOverlay } from '@/components/cameraStreamWidget/FeedStatusOverlay.tsx';
import { useCameraStreamFullscreen } from '@/components/cameraStreamWidget/useCameraStreamFullscreen.ts';
import { useCameraStreamLivePlayers } from '@/components/cameraStreamWidget/useCameraStreamLivePlayers.ts';
import { useHlsRewindPlayer } from '@/components/cameraStreamWidget/useHlsRewindPlayer.ts';
import { useVideoSnapshots } from '@/components/cameraStreamWidget/useVideoSnapshots.ts';
import { ToolbarForWidget } from '@/components/ToolbarForWidget.tsx';
import { REWIND_TIER_NAME } from '@/constants';
import { useInventory } from '@/dataAccess/rest';
import { useVideoRecall } from '@/hooks';
import { useSiteContext } from '@/providers';
import { useGamepadContext } from '@/providers/index.ts';
import { useUserSettingsStore } from '@/stores';
import { ComponentType, GenericWidgetProps } from '@/types';
import { Icon, responsive } from '@/utils';
import { addScaleInternalToUrl } from '@/utils/scaleInternal';
import { addViewerTagToUrl } from '@/utils/viewerTag';
import { CAMERA_STREAM_WIDGET } from '@/widgetComponentNames';

import { CameraHeaderTitle } from './cameraStreamWidget/CameraHeaderTitle.tsx';
import {
  StyledHeaderControls,
  StyledVideo,
  StyledVideoHeader,
} from './cameraStreamWidget/cameraStreamWidget.styles.ts';
import { CameraStreamWidgetWrapper } from './cameraStreamWidget/CameraStreamWidgetWrapper.tsx';

type CameraStreamWidgetProps = GenericWidgetProps & {
  camera: CameraInfo;
  editActionList?: IconItem[];
  /** Persisted stream `name` from layout (URI always taken from live `camera.webRtcStreams`). */
  persistedWebRtcStreamName?: string;
  /** Prefer the camera's low profile on first load, without overriding later user choices. */
  preferLowResolutionOnInitialLoad?: boolean;
  /** Persist stream name to widget config (marks layout dirty until user saves layout to DB). */
  onPersistWebRtcStreamName?: (streamName: string | undefined) => void;
  /** Landing-page layout: keep video header strip visible without hover. Omitted ⇒ unpinned. */
  topBannerPinned?: boolean;
  /** Landing-page layout: show `camera.name` in the video header. Omitted ⇒ hidden. */
  isCameraNameVisible?: boolean;
  /** Right-click on the widget surface; browser menu is suppressed when this is set. */
  onContextMenu?: MouseEventHandler<HTMLDivElement>;
  /** Camera group switching (panel, hotkeys, gamepad). Omitted ⇒ feature is absent entirely. */
  cameraSwitching?: CameraSwitching;
};

const FEEDING_STATUS_EVENT_NAME = new FeedingDeviceChanged().className;
const loadingZIndexDefault = 10;
export const CameraStreamWidget: ComponentType<CameraStreamWidgetProps> = ({
  camera,
  widgetId,
  editActionList,
  persistedWebRtcStreamName,
  preferLowResolutionOnInitialLoad = false,
  isCameraNameVisible = false,
  onPersistWebRtcStreamName,
  topBannerPinned = false,
  onContextMenu,
  cameraSwitching,
}: CameraStreamWidgetProps) => {
  const { currentSite } = useSiteContext();
  // Snapshots (capture buttons and filmstrip) ship behind the preview toggle in settings.
  const snapshotsEnabled = useUserSettingsStore(s => s.enablePreviewContent);
  const { t } = useTranslation(['cameraControl', 'common']);
  const parentId = camera.parentId ?? '';
  const { data: parent } = useInventory(camera.parentId);

  const [isVideoPaused, setIsVideoPaused] = useState(false);
  const [mode, setMode] = useState<'live' | 'rewind'>('live');
  const pauseVideoActionId = useMemo(() => uuidv4(), []);
  const liveSnapshotActionId = useMemo(() => uuidv4(), []);

  const {
    streamOptions,
    selectedStream,
    firstPlayer,
    secondPlayer,
    activePlayerIndex,
    isProfileSwitching,
    onProfileToggle,
    onPlayerReady,
    onPlayerFailed,
    videoRef,
    state,
    error,
    errorCode,
    isLoading,
    retryCount,
  } = useCameraStreamLivePlayers({
    cameraId: camera.id,
    webRtcStreams: camera.webRtcStreams,
    persistedWebRtcStreamName,
    preferredLowResolutionStreamName: camera.manualVideoProfilePair?.lowName,
    preferLowResolutionOnInitialLoad,
    onPersistWebRtcStreamName,
    jitterBufferTargetMs: camera.jitterBufferTargetMs,
    includeScaleInternal: currentSite?.includeScaleInternal,
    enabled: mode === 'live' && !isVideoPaused,
  });

  // Sibling switches reuse this instance; drop pause/rewind so camera B does not inherit A's playback.
  useEffect(() => {
    setIsVideoPaused(false);
    setMode('live');
  }, [/* effect dep */ camera.id]);

  const { enabled: videoRecallEnabled } = useVideoRecall();
  // Rewind recalls the HIGH tier and ONLY the HIGH tier, independent of whichever tier is
  // selected for live — the server pins exactly one DVR buffer per camera (always HIGH;
  // see RelaySyncService.SelectDvrTier) rather than following the live-viewed tier, since
  // different viewers can pick different live tiers independently, and there's no single
  // "currently watched" tier across them to follow. No fallback on purpose: a camera with
  // no HIGH stream simply offers no rewind (`canRewind` false below) rather than silently
  // recalling a different tier than the one the label promises.
  const recallStream = camera.webRtcStreams?.find(s => s.name === REWIND_TIER_NAME);
  const hlsRecallUri = recallStream?.hlsRecallUri ?? '';
  const dvrWindowSeconds = recallStream?.dvrWindowSeconds;
  const canRewind =
    camera.isRewindEnabled && videoRecallEnabled && Boolean(hlsRecallUri) && canPlayHlsRecall();

  const rewind = useHlsRewindPlayer({
    url: addViewerTagToUrl(
      currentSite?.includeScaleInternal ? addScaleInternalToUrl(hlsRecallUri) : hlsRecallUri
    ),
    enabled: mode === 'rewind' && canRewind,
    videoRef,
    fallbackWindowSeconds: dvrWindowSeconds,
  });
  const { requestSeekOffsetFromLive } = rewind;

  // Live (WebRTC) and rewind (HLS) share one <video>; only one runs at a time. Reset to
  // live on stream change, and whenever recall isn't available for the selected stream.
  useEffect(() => {
    setMode('live');
  }, [selectedStream?.value.webRtcStreamUri]);
  useEffect(() => {
    if (!canRewind) setMode('live');
  }, [canRewind]);

  const handleScrub = useCallback(
    (offsetFromLiveSeconds: number) => {
      setMode('rewind');
      requestSeekOffsetFromLive(offsetFromLiveSeconds);
    },
    [requestSeekOffsetFromLive]
  );
  const handleGoLive = useCallback(() => setMode('live'), []);

  // Snapshots of the current DVR frame: shown instantly from the local capture, then
  // persisted on-cluster via mediaserver-api (SCO-2405, mediamtx PVC). Tier must match
  // what the captured FRAME actually is, not whatever's selected for live view: rewind
  // always recalls HIGH (REWIND_TIER_NAME, same as recallStream above) regardless of the
  // live tier selection, which is frozen/stale while rewinding (profileToggleHidden).
  // Using the live selection unconditionally here previously tagged rewind captures LOW
  // whenever live view happened to be set to LOW.
  const snapshotCameraId = camera.id ?? '';
  const snapshotTier = mode === 'rewind' ? REWIND_TIER_NAME : (selectedStream?.value.name ?? '');
  const {
    snapshots,
    captureSnapshot,
    persistSnapshot: persistSnapshotById,
    removeSnapshot,
  } = useVideoSnapshots(videoRef, { cameraId: snapshotCameraId, tier: snapshotTier });
  const { offsetFromLiveSeconds } = rewind;
  /** DVR: cluster storage is the default — upload immediately after capture. */
  const handleSnapshot = useCallback(
    () => void captureSnapshot({ offsetFromLiveSeconds, autoPersist: true }),
    [captureSnapshot, offsetFromLiveSeconds]
  );
  /**
   * Live (WebRTC): capture at the live edge and store it on the cluster in the
   * background. Nothing opens over the live feed — the capture surfaces in the filmstrip
   * the next time the operator rewinds, which is where save/download/delete live.
   */
  const handleLiveSnapshot = useCallback(
    () => void captureSnapshot({ offsetFromLiveSeconds: 0, autoPersist: true }),
    [captureSnapshot]
  );

  const rewinding = mode === 'rewind';
  // Camera (PTZ) controls are hidden during HLS rewind, but can be force-enabled for
  // debugging via `?dvrCameraControls=1` or localStorage `dvrCameraControls=1`.
  const dvrCameraControlsDebug = useMemo(
    () =>
      typeof window !== 'undefined' &&
      (new URLSearchParams(window.location.search).get('dvrCameraControls') === '1' ||
        window.localStorage.getItem('dvrCameraControls') === '1'),
    []
  );
  const showCameraControls = !rewinding || dvrCameraControlsDebug;

  const errorMessage = useMemo(() => {
    if (errorCode === 400) return t('unableToStartStream');
    if (errorCode === 404) return t('streamNotAvailable');
    if (errorCode != null) return `${t('connectionFailed')} - ${errorCode}`;
    if (state === 'failed') return t('connectionFailed');
    if (state === 'disconnected' || state === 'closed') return t('connectionLost');
    return error;
  }, [errorCode, error, state, t]);
  const { isGamepadConnected } = useGamepadContext();
  const navigate = useNavigate();

  const isError = useMemo(() => {
    if (mode === 'rewind') return rewind.error !== null;
    return (
      !isVideoPaused &&
      (error !== null || state === 'closed' || state === 'disconnected' || state === 'failed')
    );
  }, [mode, rewind.error, error, isVideoPaused, state]);

  // While rewinding, present the widget as a connected stream (the recall video is the
  // active source) so WebRTC-oriented overlays and fullscreen gating behave correctly.
  const effectiveState = mode === 'rewind' ? 'connected' : state;
  const isLoadingEffective =
    mode === 'rewind' ? canRewind && !rewind.isReady && rewind.error === null : isLoading;

  const toggleVideoPaused = useCallback(() => {
    setIsVideoPaused(current => !current);
  }, []);

  // The 3-dot menu only offers a live snapshot when the WebRTC stream is actually
  // showing frames (the DVR player has its own snapshot button in the scrubber bar).
  const canSnapshotLive =
    snapshotsEnabled && mode === 'live' && !isVideoPaused && state === 'connected';

  const canToggleFullscreen = effectiveState === 'connected' || isVideoPaused;
  const { handleDoubleClick, toggleFullscreen, usePortalForWidgetMenu } = useCameraStreamFullscreen(
    { canToggle: canToggleFullscreen }
  );

  const { results: feedingStatus, isLoading: feedingStatusIsLoading } =
    useGetSensorsDataByEventAndIds<ISiteControlDeviceChanged<FeedingDeviceInfo>>(
      [parentId],
      FEEDING_STATUS_EVENT_NAME
    );

  const actionList = useMemo(() => {
    const { label, Icon: PauseToggleIcon } = getVideoPauseToggle(isVideoPaused, t);
    return [
      ...(editActionList ? editActionList : []),
      {
        label,
        Icon: PauseToggleIcon,
        id: pauseVideoActionId,
        onClick: toggleVideoPaused,
      },
      // Live (WebRTC) snapshot — the DVR player has its own scrubber-bar button. Hidden
      // while paused (no fresh frame to grab) and while the stream isn't connected.
      ...(canSnapshotLive
        ? [
            {
              label: t('snapshot.takeLive'),
              Icon: Icon.Screenshot,
              id: liveSnapshotActionId,
              onClick: handleLiveSnapshot,
            },
          ]
        : []),
      ...(isGamepadConnected
        ? [
            {
              label: t('showGamepadLayout'),
              Icon: Icon.Eye,
              id: uuidv4(),
              onClick: () => {
                navigate({ to: '/gamepadLayoutScreen' });
              },
            },
          ]
        : []),
    ];
  }, [
    editActionList,
    isGamepadConnected,
    isVideoPaused,
    pauseVideoActionId,
    toggleVideoPaused,
    canSnapshotLive,
    liveSnapshotActionId,
    handleLiveSnapshot,
    t,
    navigate,
  ]);

  const headerName = isCameraNameVisible ? camera.name : parent?.name;
  const headerTooltip = useMemo(() => {
    return (
      <FlexAligner
        direction={'column'}
        gap={responsive.spacing.xsmall}
        style={{ paddingLeft: responsive.spacing.medium }}
      >
        {parent && (
          <Typography type={'body_small'}>
            {t('cameraControl:penName', { penName: parent.name })}
          </Typography>
        )}
        <Typography type={'body_small'}>
          {t('cameraControl:cameraNameWithQuality', {
            cameraName: camera.name,
            quality: selectedStream?.label,
          })}
        </Typography>
      </FlexAligner>
    );
  }, [camera, parent, selectedStream?.label, t]);

  return (
    <CameraStreamWidgetWrapper
      aspectRatio={selectedStream?.value.aspectRatio ?? AspectRatio.Standard}
      loadingZIndex={loadingZIndexDefault}
      topBannerPinned={topBannerPinned}
      camera={camera}
      isError={isError}
      isVideoPaused={isVideoPaused}
      error={
        mode === 'rewind'
          ? rewind.error
            ? t('connectionFailed')
            : ''
          : retryCount > 0
            ? `${errorMessage ?? t('connectionLost')} (${t('connecting')})`
            : (errorMessage ?? '')
      }
      isLoading={isLoadingEffective}
      state={effectiveState}
      showCameraControls={showCameraControls}
      onDoubleClick={handleDoubleClick}
      onToggleFullscreen={toggleFullscreen}
      onContextMenu={onContextMenu}
      cameraSwitching={cameraSwitching}
    >
      <StyledVideoHeader
        gap={responsive.spacing['3xlarge']}
        alignment='center'
        spaceBetween
        $zIndex={loadingZIndexDefault + 2}
      >
        <CameraHeaderTitle
          currentSite={currentSite}
          headerName={headerName}
          headerTooltip={headerTooltip}
        />
        <CameraStreamWinchStatus camera={camera} usePortal={usePortalForWidgetMenu} />
        <StyledHeaderControls>
          <CameraSensorReading camera={camera} />
          <CameraStreamWebRtcStatus
            streamUri={selectedStream?.value.webRtcStreamUri}
            state={state}
            isVideoPaused={isVideoPaused}
            isRewinding={rewinding}
            cameraId={camera.id}
            // Rewind always recalls the HIGH tier, so that is the source whose health applies.
            streamName={rewinding ? REWIND_TIER_NAME : selectedStream?.value.name}
            webRtcStreams={camera.webRtcStreams}
            usePortal={usePortalForWidgetMenu}
          />
          {!rewinding && (
            <ToolbarForWidget
              widgetId={widgetId}
              toolbarActionList={actionList}
              usePortal={usePortalForWidgetMenu}
            />
          )}
        </StyledHeaderControls>
      </StyledVideoHeader>

      <StyledVideo
        ref={firstPlayer.videoRef}
        autoPlay
        playsInline
        muted
        $visible={activePlayerIndex === 0}
        aria-hidden={activePlayerIndex !== 0}
        onPlaying={() => onPlayerReady(0)}
        onError={() => onPlayerFailed(0)}
      />
      <StyledVideo
        ref={secondPlayer.videoRef}
        autoPlay
        playsInline
        muted
        $visible={activePlayerIndex === 1}
        aria-hidden={activePlayerIndex !== 1}
        onPlaying={() => onPlayerReady(1)}
        onError={() => onPlayerFailed(1)}
      />
      <CameraStreamSideControls
        camera={camera}
        cameraSwitching={cameraSwitching}
        selectedStream={selectedStream}
        streamOptions={streamOptions}
        profileSwitching={isProfileSwitching}
        // Paused (live) keeps the toggle visible but inert; rewind removes it entirely —
        // recall is always the HIGH tier, so there is no tier choice to offer.
        profileToggleDisabled={isVideoPaused}
        profileToggleHidden={rewinding}
        onProfileToggle={onProfileToggle}
        zIndex={loadingZIndexDefault + 3}
      />
      {mode === 'live' && (
        <CameraStreamPauseToggleButton
          isVideoPaused={isVideoPaused}
          onToggle={toggleVideoPaused}
          zIndex={loadingZIndexDefault + 3}
        />
      )}
      {canRewind && (
        <CameraStreamDvrControls
          mode={mode}
          rewind={rewind}
          fallbackWindowSeconds={dvrWindowSeconds}
          onScrub={handleScrub}
          onGoLive={handleGoLive}
          onSnapshot={snapshotsEnabled ? handleSnapshot : undefined}
          zIndex={loadingZIndexDefault + 3}
        />
      )}
      {snapshotsEnabled && (
        <CameraSnapshotsOverlay
          snapshots={snapshots}
          onRemove={removeSnapshot}
          onPersist={persistSnapshotById}
          cameraName={camera.name}
          historyEnabled={rewinding}
          cameraId={snapshotCameraId}
          zIndex={loadingZIndexDefault + 4}
        />
      )}

      <FeedStatusOverlay
        loading={feedingStatusIsLoading}
        feedingStatus={feedingStatus?.[parentId]?.deviceInfo}
      />
    </CameraStreamWidgetWrapper>
  );
};

CameraStreamWidget.componentName = CAMERA_STREAM_WIDGET;
